import React, { useState, useMemo, useRef } from "react";
import { TreeSelect, Spin, ConfigProvider } from "antd";
import debounce from "lodash/debounce";
import { get } from "../../../utils/request";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlightIcon from "@mui/icons-material/Flight";
import { FaExchangeAlt } from "react-icons/fa";
import "./flights-location-selector.scss";

const allLocations = [];

export default function FlightsLocationSelector({
  index,
  selections,
  setSelections,
  setFlightsLocationSelections
}) {
  const [loadingMap, setLoadingMap] = useState({ from: false, to: false });
  const fetchRef = useRef(0);

  async function fetchSearchLocations(query) {
    try {
      const result = await get("/flights/searchLocations", { query });
      return result;
    } catch (err) {
      return [];
    }
  }

  const buildTreeData = (locations) => {
    return locations.map((loc) => ({
      title: (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div
            style={{ display: "flex", alignItems: "center", padding: "2px 0" }}
          >
            <LocationOnIcon
              style={{ color: "#1f1f1f", fontSize: 16, marginRight: 6 }}
            />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#1f1f1f" }}>
                {loc.cityName}
                <span
                  style={{ fontWeight: 400, color: "#8c8c8c", marginLeft: 4 }}
                >
                  · All airports
                </span>
              </div>
              <div style={{ fontSize: 13, color: "#8c8c8c", marginTop: 2 }}>
                {loc.countryName}
              </div>
            </div>
          </div>

          <div
            style={{
              height: 0.5,
              backgroundColor: "rgba(200,200,200,0.6)",
              marginLeft: 20,
              marginRight: 2,
              marginTop: 8,
            }}
          />
        </div>
      ),
      value: `city-${loc.cityCode}`,
      key: `city-${loc.cityCode}`,
      city: loc,
      airport: null,
      children: loc.airports.map((a) => ({
        title: (
          <div style={{ display: "flex", flexDirection: "column", marginTop: 4 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FlightIcon style={{ color: "#1f1f1f", fontSize: 16 }} />

              <span style={{ fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{a.airportCode} </span>
                <span style={{ fontWeight: 400, wordBreak: "break-word" }}>
                  {a.airportName}
                </span>
              </span>
            </div>

            <div
              style={{
                height: 0.5,
                backgroundColor: "rgba(200,200,200,0.6)",
                marginLeft: 20,
                marginRight: 2,
                marginTop: 8,
              }}
            />
          </div>
        ),
        value: `airport-${a.airportCode}`,
        key: `airport-${a.airportCode}`,
        city: loc,
        airport: a,
        children: [],
      })),
    }));
  };

  const defaultTreeData = buildTreeData(defaultLocations);
  const [optionsMap, setOptionsMap] = useState({ from: defaultTreeData, to: defaultTreeData });

  const handleSearch = useMemo(() => {
    const load = async (type, value) => {
      if (!value || value.trim().length < 2) {
        setOptionsMap((prev) => ({ ...prev, [type]: buildTreeData(allLocations) }));
        setLoadingMap((prev) => ({ ...prev, [type]: false }));
        return;
      }

      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setLoadingMap((prev) => ({ ...prev, [type]: true }));

      try {
        const result = await fetchSearchLocations(value);
        if (fetchId !== fetchRef.current) return;

        setOptionsMap((prev) => ({ ...prev, [type]: buildTreeData(result) }));
      } catch {
        setOptionsMap((prev) => ({ ...prev, [type]: buildTreeData([]) }));
      } finally {
        setLoadingMap((prev) => ({ ...prev, [type]: false }));
      }
    };
    return debounce(load, 400);
  }, []);

  const handleSelect = (type, value, node) => {
    setSelections((prev) => {
      const newSel = [...prev];
      newSel[index] = {
        ...newSel[index],
        [type]: {
          countryCode: node.city.countryCode,
          countryName: node.city.countryName,
          cityCode: node.city.cityCode,
          cityName: node.city.cityName,
          airportCode: node?.airport?.airportCode,
          airportName: node?.airport?.airportName
        },
      };
      return newSel;
    });

    if (!!setFlightsLocationSelections) {
      setFlightsLocationSelections((prev) => {
        const newSel = [...prev];
        newSel[index] = {
          ...newSel[index],
          [type]: {
            countryCode: node.city.countryCode,
            countryName: node.city.countryName,
            cityCode: node.city.cityCode,
            cityName: node.city.cityName,
            airportCode: node?.airport?.airportCode,
            airportName: node?.airport?.airportName
          },
        };
        return newSel;
      })
    }
  };

  const getAllKeys = (nodes) => {
    let keys = [];
    nodes.forEach(node => {
      keys.push(node.key);
      if (node.children) {
        keys = keys.concat(getAllKeys(node.children));
      }
    });
    return keys;
  };

  function getLocationName(location) {
    if (!location) return "";

    if (location.cityName === location.countryName) {
      return location.cityName;
    }

    return `${location.cityName}, ${location.countryName}`;
  }

  const renderTreeSelect = (type) => {
    const selected = selections?.[index]?.[type];

    return (
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#CBA135",
            colorPrimaryBorder: "#CBA135",
            colorPrimaryHover: "#CBA135",
          },
          components: {
            Select: {
              controlHeight: 48,
            },
          },
        }}
      >
        <TreeSelect
          style={{ width: "100%" }}
          value={
            selected
              ? !!selected?.airportCode
                ? `${getLocationName(selected)} (${selected.airportCode})`
                : `${getLocationName(selected)}`
              : undefined
          }
          dropdownStyle={{ maxHeight: 400, overflow: "auto", minWidth: 300 }}
          placeholder={`Select location`}
          showSearch
          treeDefaultExpandAll={true}
          filterTreeNode={false}
          onSearch={(value) => handleSearch(type, value)}
          onSelect={handleSelect.bind(null, type)}
          treeData={optionsMap[type].length !== 0 ? optionsMap[type] : defaultTreeData}
          treeNodeLabelProp="title"
          suffixIcon={loadingMap[type] ? <Spin size="small" /> : undefined}
          treeExpandedKeys={getAllKeys(optionsMap[type])}
          switcherIcon={null}
          popupMatchSelectWidth={300}
        />
      </ConfigProvider>
    );
  };

  const swapLocations = () => {
    setSelections((prev) => {
      const newSel = [...prev];
      const temp = newSel[index].from;
      newSel[index].from = newSel[index].to;
      newSel[index].to = temp;
      return newSel;
    });

    if (!!setFlightsLocationSelections) {
      setFlightsLocationSelections((prev) => {
        const newSel = [...prev];
        const temp = newSel[index].from;
        newSel[index].from = newSel[index].to;
        newSel[index].to = temp;
        return newSel;
      });
    }
  };

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <div style={{ width: "45%" }}>{renderTreeSelect("from")}</div>

      <div
        className="swap-icon"
        style={{ cursor: "pointer", fontSize: 18 }}
        onClick={swapLocations}
      >
        <FaExchangeAlt color="#c9a43b" />
      </div>

      <div style={{ width: "45%" }}>{renderTreeSelect("to")}</div>
    </div>
  );
}

const defaultLocations = [
  {
    "cityName": "Tokyo",
    "cityCode": "TYO",
    "countryName": "Japan",
    "countryCode": "JP",
    "airports": [
      {
        "airportName": "Tokyo Haneda International Airport",
        "airportCode": "HND"
      },
      {
        "airportName": "Narita International Airport",
        "airportCode": "NRT"
      }
    ]
  },
  {
    "cityName": "Singapore",
    "cityCode": "SIN",
    "countryName": "Singapore",
    "countryCode": "SG",
    "airports": [
      {
        "airportName": "Singapore Changi Airport",
        "airportCode": "SIN"
      }
    ]
  },
  {
    "cityName": "Bangkok",
    "cityCode": "BKK",
    "countryName": "Thailand",
    "countryCode": "TH",
    "airports": [
      {
        "airportName": "Suvarnabhumi Airport",
        "airportCode": "BKK"
      },
      {
        "airportName": "Don Mueang International Airport",
        "airportCode": "DMK"
      }
    ]
  },
  {
    "cityName": "Hong Kong",
    "cityCode": "HKG",
    "countryName": "Hong Kong",
    "countryCode": "HK",
    "airports": [
      {
        "airportName": "Hong Kong International Airport",
        "airportCode": "HKG"
      }
    ]
  },
  {
    "cityName": "Seoul",
    "cityCode": "SEL",
    "countryName": "South Korea",
    "countryCode": "KR",
    "airports": [
      {
        "airportName": "Gimpo International Airport",
        "airportCode": "GMP"
      },
      {
        "airportName": "Incheon International Airport",
        "airportCode": "ICN"
      }
    ]
  },
  {
    "cityName": "Dubai",
    "cityCode": "DXB",
    "countryName": "United Arab Emirates",
    "countryCode": "AE",
    "airports": [
      {
        "airportName": "Dubai International Airport",
        "airportCode": "DXB"
      },
      {
        "airportName": "Al Maktoum International Airport",
        "airportCode": "DWC"
      }
    ]
  },
  {
    "cityName": "Delhi",
    "cityCode": "DEL",
    "countryName": "India",
    "countryCode": "IN",
    "airports": [
      {
        "airportName": "Indira Gandhi International Airport",
        "airportCode": "DEL"
      }
    ]
  },
  {
    "cityName": "Mumbai",
    "cityCode": "BOM",
    "countryName": "India",
    "countryCode": "IN",
    "airports": [
      {
        "airportName": "Chhatrapati Shivaji International Airport",
        "airportCode": "BOM"
      },
      {
        "airportName": "Navi Mumbai International Airport",
        "airportCode": "NMI"
      }
    ]
  },
  {
    "cityName": "Jakarta",
    "cityCode": "JKT",
    "countryName": "Indonesia",
    "countryCode": "ID",
    "airports": [
      {
        "airportName": "Soekarno-Hatta International Airport",
        "airportCode": "CGK"
      }
    ]
  },
  {
    "cityName": "Kuala Lumpur",
    "cityCode": "KUL",
    "countryName": "Malaysia",
    "countryCode": "MY",
    "airports": [
      {
        "airportName": "Kuala Lumpur International Airport",
        "airportCode": "KUL"
      },
      {
        "airportName": "Sultan Abdul Aziz Shah International Airport",
        "airportCode": "SZB"
      }
    ]
  },
  {
    "cityName": "Karachi",
    "cityCode": "KHI",
    "countryName": "Pakistan",
    "countryCode": "PK",
    "airports": [
      {
        "airportName": "Jinnah International Airport",
        "airportCode": "KHI"
      }
    ]
  },
  {
    "cityName": "Lahore",
    "cityCode": "LHE",
    "countryName": "Pakistan",
    "countryCode": "PK",
    "airports": [
      {
        "airportName": "Allama Iqbal International Airport",
        "airportCode": "LHE"
      }
    ]
  },
  {
    "cityName": "Islamabad",
    "cityCode": "ISB",
    "countryName": "Pakistan",
    "countryCode": "PK",
    "airports": [
      {
        "airportName": "Islamabad International Airport",
        "airportCode": "ISB"
      }
    ]
  },
  {
    "cityName": "Beijing",
    "cityCode": "BJS",
    "countryName": "China",
    "countryCode": "CN",
    "airports": [
      {
        "airportName": "Beijing Capital International Airport",
        "airportCode": "PEK"
      },
      {
        "airportName": "Beijing Daxing International Airport",
        "airportCode": "PKX"
      }
    ]
  },
  {
    "cityName": "Shanghai",
    "cityCode": "SHA",
    "countryName": "China",
    "countryCode": "CN",
    "airports": [
      {
        "airportName": "Shanghai Pudong International Airport",
        "airportCode": "PVG"
      },
      {
        "airportName": "Shanghai Hongqiao International Airport",
        "airportCode": "SHA"
      }
    ]
  },
  {
    "cityName": "Guangzhou",
    "cityCode": "CAN",
    "countryName": "China",
    "countryCode": "CN",
    "airports": [
      {
        "airportName": "Guangzhou Baiyun International Airport",
        "airportCode": "CAN"
      }
    ]
  },
  {
    "cityName": "Istanbul",
    "cityCode": "IST",
    "countryName": "Turkey",
    "countryCode": "TR",
    "airports": [
      {
        "airportName": "Istanbul Sabiha Gökçen International Airport",
        "airportCode": "SAW"
      },
      {
        "airportName": "İstanbul Airport",
        "airportCode": "IST"
      }
    ]
  },
  {
    "cityName": "Doha",
    "cityCode": "DOH",
    "countryName": "Qatar",
    "countryCode": "QA",
    "airports": [
      {
        "airportName": "Hamad International Airport",
        "airportCode": "DOH"
      }
    ]
  },
  {
    "cityName": "Taipei",
    "cityCode": "TPE",
    "countryName": "Taiwan",
    "countryCode": "TW",
    "airports": [
      {
        "airportName": "Taiwan Taoyuan International Airport",
        "airportCode": "TPE"
      },
      {
        "airportName": "Taipei Songshan Airport",
        "airportCode": "TSA"
      }
    ]
  },
  {
    "cityName": "Manila",
    "cityCode": "MNL",
    "countryName": "Philippines",
    "countryCode": "PH",
    "airports": [
      {
        "airportName": "Ninoy Aquino International Airport",
        "airportCode": "MNL"
      }
    ]
  },
  {
    "cityName": "Riyadh",
    "cityCode": "RUH",
    "countryName": "Saudi Arabia",
    "countryCode": "SA",
    "airports": [
      {
        "airportName": "King Khalid International Airport",
        "airportCode": "RUH"
      }
    ]
  },
  {
    "cityName": "Jeddah",
    "cityCode": "JED",
    "countryName": "Saudi Arabia",
    "countryCode": "SA",
    "airports": [
      {
        "airportName": "King Abdulaziz International Airport",
        "airportCode": "JED"
      }
    ]
  },
  {
    "cityName": "Amman",
    "cityCode": "AMM",
    "countryName": "Jordan",
    "countryCode": "JO",
    "airports": [
      {
        "airportName": "Queen Alia International Airport",
        "airportCode": "AMM"
      }
    ]
  },
  {
    "cityName": "Damascus",
    "cityCode": "DAM",
    "countryName": "Syria",
    "countryCode": "SY",
    "airports": [
      {
        "airportName": "Damascus International Airport",
        "airportCode": "DAM"
      }
    ]
  },
  {
    "cityName": "Beirut",
    "cityCode": "BEY",
    "countryName": "Lebanon",
    "countryCode": "LB",
    "airports": [
      {
        "airportName": "Beirut Rafic Hariri International Airport",
        "airportCode": "BEY"
      }
    ]
  },
  {
    "cityName": "Hanoi",
    "cityCode": "HAN",
    "countryName": "Vietnam",
    "countryCode": "VN",
    "airports": [
      {
        "airportName": "Noi Bai International Airport",
        "airportCode": "HAN"
      }
    ]
  },
  {
    "cityName": "Ho Chi Minh City",
    "cityCode": "SGN",
    "countryName": "Vietnam",
    "countryCode": "VN",
    "airports": [
      {
        "airportName": "Tan Son Nhat International Airport",
        "airportCode": "SGN"
      }
    ]
  },
  {
    "cityName": "Phnom Penh",
    "cityCode": "KTI",
    "countryName": "Cambodia",
    "countryCode": "KH",
    "airports": [
      {
        "airportName": "Techo International Airport",
        "airportCode": "KTI"
      }
    ]
  },
  {
    "cityName": "Kathmandu",
    "cityCode": "KTM",
    "countryName": "Nepal",
    "countryCode": "NP",
    "airports": [
      {
        "airportName": "Tribhuvan International Airport",
        "airportCode": "KTM"
      }
    ]
  },
  {
    "cityName": "Dhaka",
    "cityCode": "DAC",
    "countryName": "Bangladesh",
    "countryCode": "BD",
    "airports": [
      {
        "airportName": "Hazrat Shahjalal International Airport",
        "airportCode": "DAC"
      }
    ]
  },
  {
    "cityName": "Colombo",
    "cityCode": "CMB",
    "countryName": "Sri Lanka",
    "countryCode": "LK",
    "airports": [
      {
        "airportName": "Bandaranaike International Colombo Airport",
        "airportCode": "CMB"
      }
    ]
  },
  {
    "cityName": "Male",
    "cityCode": "MLE",
    "countryName": "Maldives",
    "countryCode": "MV",
    "airports": [
      {
        "airportName": "Malé International Airport",
        "airportCode": "MLE"
      }
    ]
  },
  {
    "cityName": "Muscat",
    "cityCode": "MCT",
    "countryName": "Oman",
    "countryCode": "OM",
    "airports": [
      {
        "airportName": "Muscat International Airport",
        "airportCode": "MCT"
      }
    ]
  },
  {
    "cityName": "Abu Dhabi",
    "cityCode": "AUH",
    "countryName": "United Arab Emirates",
    "countryCode": "AE",
    "airports": [
      {
        "airportName": "Zayed International Airport",
        "airportCode": "AUH"
      }
    ]
  },
  {
    "cityName": "Tehran",
    "cityCode": "THR",
    "countryName": "Iran",
    "countryCode": "IR",
    "airports": [
      {
        "airportName": "Imam Khomeini International Airport",
        "airportCode": "IKA"
      },
      {
        "airportName": "Mehrabad International Airport",
        "airportCode": "THR"
      }
    ]
  },
  {
    "cityName": "London",
    "cityCode": "LON",
    "countryName": "United Kingdom",
    "countryCode": "GB",
    "airports": [
      {
        "airportName": "London Heathrow Airport",
        "airportCode": "LHR"
      },
      {
        "airportName": "London Gatwick Airport",
        "airportCode": "LGW"
      },
      {
        "airportName": "London Stansted Airport",
        "airportCode": "STN"
      },
      {
        "airportName": "London City Airport",
        "airportCode": "LCY"
      },
      {
        "airportName": "London Luton Airport",
        "airportCode": "LTN"
      },
      {
        "airportName": "London Southend Airport",
        "airportCode": "SEN"
      }
    ]
  },
  {
    "cityName": "Paris",
    "cityCode": "PAR",
    "countryName": "France",
    "countryCode": "FR",
    "airports": [
      {
        "airportName": "Charles de Gaulle International Airport",
        "airportCode": "CDG"
      },
      {
        "airportName": "Paris-Orly Airport",
        "airportCode": "ORY"
      },
      {
        "airportName": "Beauvais Tillé airport",
        "airportCode": "BVA"
      }
    ]
  },
  {
    "cityName": "Amsterdam",
    "cityCode": "AMS",
    "countryName": "Netherlands",
    "countryCode": "NL",
    "airports": [
      {
        "airportName": "Amsterdam Airport Schiphol",
        "airportCode": "AMS"
      }
    ]
  },
  {
    "cityName": "Frankfurt",
    "cityCode": "FRA",
    "countryName": "Germany",
    "countryCode": "DE",
    "airports": [
      {
        "airportName": "Frankfurt Airport",
        "airportCode": "FRA"
      },
      {
        "airportName": "Frankfurt-Hahn Airport",
        "airportCode": "HHN"
      }
    ]
  },
  {
    "cityName": "Munich",
    "cityCode": "MUC",
    "countryName": "Germany",
    "countryCode": "DE",
    "airports": [
      {
        "airportName": "Munich Airport",
        "airportCode": "MUC"
      }
    ]
  },
  {
    "cityName": "Madrid",
    "cityCode": "MAD",
    "countryName": "Spain",
    "countryCode": "ES",
    "airports": [
      {
        "airportName": "Adolfo Suárez Madrid–Barajas Airport",
        "airportCode": "MAD"
      }
    ]
  },
  {
    "cityName": "Barcelona",
    "cityCode": "BCN",
    "countryName": "Spain",
    "countryCode": "ES",
    "airports": [
      {
        "airportName": "Josep Tarradellas Barcelona-El Prat Airport",
        "airportCode": "BCN"
      }
    ]
  },
  {
    "cityName": "Rome",
    "cityCode": "ROM",
    "countryName": "Italy",
    "countryCode": "IT",
    "airports": [
      {
        "airportName": "Rome–Fiumicino Leonardo da Vinci International Airport",
        "airportCode": "FCO"
      },
      {
        "airportName": "Ciampino–G. B. Pastine International Airport",
        "airportCode": "CIA"
      }
    ]
  },
  {
    "cityName": "Moscow",
    "cityCode": "MOW",
    "countryName": "Russia",
    "countryCode": "RU",
    "airports": [
      {
        "airportName": "Domodedovo International Airport",
        "airportCode": "DME"
      },
      {
        "airportName": "Sheremetyevo International Airport",
        "airportCode": "SVO"
      },
      {
        "airportName": "Vnukovo International Airport",
        "airportCode": "VKO"
      }
    ]
  },
  {
    "cityName": "Zurich",
    "cityCode": "ZRH",
    "countryName": "Switzerland",
    "countryCode": "CH",
    "airports": [
      {
        "airportName": "Zürich Airport",
        "airportCode": "ZRH"
      }
    ]
  },
  {
    "cityName": "Geneva",
    "cityCode": "GVA",
    "countryName": "Switzerland",
    "countryCode": "CH",
    "airports": [
      {
        "airportName": "Geneva Cointrin International Airport",
        "airportCode": "GVA"
      }
    ]
  },
  {
    "cityName": "Vienna",
    "cityCode": "VIE",
    "countryName": "Austria",
    "countryCode": "AT",
    "airports": [
      {
        "airportName": "Vienna International Airport",
        "airportCode": "VIE"
      }
    ]
  },
  {
    "cityName": "Dublin",
    "cityCode": "DUB",
    "countryName": "Ireland",
    "countryCode": "IE",
    "airports": [
      {
        "airportName": "Dublin Airport",
        "airportCode": "DUB"
      }
    ]
  },
  {
    "cityName": "Lisbon",
    "cityCode": "LIS",
    "countryName": "Portugal",
    "countryCode": "PT",
    "airports": [
      {
        "airportName": "Lisbon Humberto Delgado Airport",
        "airportCode": "LIS"
      }
    ]
  },
  {
    "cityName": "Copenhagen",
    "cityCode": "CPH",
    "countryName": "Denmark",
    "countryCode": "DK",
    "airports": [
      {
        "airportName": "Copenhagen Kastrup Airport",
        "airportCode": "CPH"
      }
    ]
  },
  {
    "cityName": "Oslo",
    "cityCode": "OSL",
    "countryName": "Norway",
    "countryCode": "NO",
    "airports": [
      {
        "airportName": "Oslo-Gardermoen International Airport",
        "airportCode": "OSL"
      },
      {
        "airportName": "Sandefjord Airport, Torp",
        "airportCode": "TRF"
      }
    ]
  },
  {
    "cityName": "Stockholm",
    "cityCode": "STO",
    "countryName": "Sweden",
    "countryCode": "SE",
    "airports": [
      {
        "airportName": "Stockholm-Arlanda Airport",
        "airportCode": "ARN"
      },
      {
        "airportName": "Stockholm-Bromma Airport",
        "airportCode": "BMA"
      },
      {
        "airportName": "Stockholm Skavsta Airport",
        "airportCode": "NYO"
      },
      {
        "airportName": "Stockholm Västerås Airport",
        "airportCode": "VST"
      }
    ]
  },
  {
    "cityName": "Helsinki",
    "cityCode": "HEL",
    "countryName": "Finland",
    "countryCode": "FI",
    "airports": [
      {
        "airportName": "Helsinki Vantaa Airport",
        "airportCode": "HEL"
      }
    ]
  },
  {
    "cityName": "Warsaw",
    "cityCode": "WAW",
    "countryName": "Poland",
    "countryCode": "PL",
    "airports": [
      {
        "airportName": "Warsaw Chopin Airport",
        "airportCode": "WAW"
      }
    ]
  },
  {
    "cityName": "Prague",
    "cityCode": "PRG",
    "countryName": "Czech Republic",
    "countryCode": "CZ",
    "airports": [
      {
        "airportName": "Václav Havel Airport Prague",
        "airportCode": "PRG"
      }
    ]
  },
  {
    "cityName": "Athens",
    "cityCode": "ATH",
    "countryName": "Greece",
    "countryCode": "GR",
    "airports": [
      {
        "airportName": "Athens Eleftherios Venizelos International Airport",
        "airportCode": "ATH"
      }
    ]
  },
  {
    "cityName": "Brussels",
    "cityCode": "BRU",
    "countryName": "Belgium",
    "countryCode": "BE",
    "airports": [
      {
        "airportName": "Brussels Airport",
        "airportCode": "BRU"
      },
      {
        "airportName": "Brussels South Charleroi Airport",
        "airportCode": "CRL"
      }
    ]
  },
  {
    "cityName": "Budapest",
    "cityCode": "BUD",
    "countryName": "Hungary",
    "countryCode": "HU",
    "airports": [
      {
        "airportName": "Budapest Liszt Ferenc International Airport",
        "airportCode": "BUD"
      }
    ]
  },
  {
    "cityName": "Reykjavik",
    "cityCode": "REK",
    "countryName": "Iceland",
    "countryCode": "IS",
    "airports": [
      {
        "airportName": "Keflavik International Airport",
        "airportCode": "KEF"
      },
      {
        "airportName": "Reykjavík Domestic Airport",
        "airportCode": "RKV"
      }
    ]
  },
  {
    "cityName": "Manchester",
    "cityCode": "MAN",
    "countryName": "United Kingdom",
    "countryCode": "GB",
    "airports": [
      {
        "airportName": "Manchester Airport",
        "airportCode": "MAN"
      }
    ]
  },
  {
    "cityName": "Milan",
    "cityCode": "MIL",
    "countryName": "Italy",
    "countryCode": "IT",
    "airports": [
      {
        "airportName": "Milan Malpensa International Airport",
        "airportCode": "MXP"
      },
      {
        "airportName": "Milano Linate Airport",
        "airportCode": "LIN"
      },
      {
        "airportName": "Milan Bergamo Airport / Antonio Locatelli Air Base",
        "airportCode": "BGY"
      },
      {
        "airportName": "Parma Airport",
        "airportCode": "PMF"
      }
    ]
  },
  {
    "cityName": "New York",
    "cityCode": "NYC",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "Newark Liberty International Airport",
        "airportCode": "EWR"
      },
      {
        "airportName": "John F Kennedy International Airport",
        "airportCode": "JFK"
      },
      {
        "airportName": "LaGuardia Airport",
        "airportCode": "LGA"
      }
    ]
  },
  {
    "cityName": "Los Angeles",
    "cityCode": "LAX",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "Los Angeles International Airport",
        "airportCode": "LAX"
      },
      {
        "airportName": "Ontario International Airport",
        "airportCode": "ONT"
      }
    ]
  },
  {
    "cityName": "Chicago",
    "cityCode": "CHI",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "Chicago O'Hare International Airport",
        "airportCode": "ORD"
      },
      {
        "airportName": "Chicago Midway International Airport",
        "airportCode": "MDW"
      },
      {
        "airportName": "Chicago Rockford International Airport",
        "airportCode": "RFD"
      }
    ]
  },
  {
    "cityName": "Toronto",
    "cityCode": "YTO",
    "countryName": "Canada",
    "countryCode": "CA",
    "airports": [
      {
        "airportName": "Toronto Lester B. Pearson International Airport",
        "airportCode": "YYZ"
      },
      {
        "airportName": "Billy Bishop Toronto City Centre Airport",
        "airportCode": "YTZ"
      },
      {
        "airportName": "Region of Waterloo International Airport",
        "airportCode": "YKF"
      },
      {
        "airportName": "John C. Munro Hamilton International Airport",
        "airportCode": "YHM"
      }
    ]
  },
  {
    "cityName": "Vancouver",
    "cityCode": "YVR",
    "countryName": "Canada",
    "countryCode": "CA",
    "airports": [
      {
        "airportName": "Vancouver International Airport",
        "airportCode": "YVR"
      },
      {
        "airportName": "Abbotsford International Airport",
        "airportCode": "YXX"
      },
      {
        "airportName": "Vancouver Harbour Water Aerodrome",
        "airportCode": "CXH"
      }
    ]
  },
  {
    "cityName": "Montreal",
    "cityCode": "YMQ",
    "countryName": "Canada",
    "countryCode": "CA",
    "airports": [
      {
        "airportName": "Montreal / Pierre Elliott Trudeau International Airport",
        "airportCode": "YUL"
      },
      {
        "airportName": "Montréal / Saint-Hubert Metropolitan Airport",
        "airportCode": "YHU"
      }
    ]
  },
  {
    "cityName": "Mexico City",
    "cityCode": "MEX",
    "countryName": "Mexico",
    "countryCode": "MX",
    "airports": [
      {
        "airportName": "Benito Juárez International Airport",
        "airportCode": "MEX"
      },
      {
        "airportName": "Adolfo López Mateos International Airport",
        "airportCode": "TLC"
      }
    ]
  },
  {
    "cityName": "Atlanta",
    "cityCode": "ATL",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "Hartsfield Jackson Atlanta International Airport",
        "airportCode": "ATL"
      }
    ]
  },
  {
    "cityName": "Denver",
    "cityCode": "DEN",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "Denver International Airport",
        "airportCode": "DEN"
      }
    ]
  },
  {
    "cityName": "Miami",
    "cityCode": "MIA",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "Miami International Airport",
        "airportCode": "MIA"
      }
    ]
  },
  {
    "cityName": "Seattle",
    "cityCode": "SEA",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "Seattle–Tacoma International Airport",
        "airportCode": "SEA"
      },
      {
        "airportName": "Kenmore Air Harbor Seaplane Base",
        "airportCode": "LKE"
      },
      {
        "airportName": "Boeing Field King County International Airport",
        "airportCode": "BFI"
      },
      {
        "airportName": "Seattle Paine Field International Airport",
        "airportCode": "PAE"
      }
    ]
  },
  {
    "cityName": "Houston",
    "cityCode": "HOU",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "George Bush Intercontinental Houston Airport",
        "airportCode": "IAH"
      },
      {
        "airportName": "William P Hobby Airport",
        "airportCode": "HOU"
      }
    ]
  },
  {
    "cityName": "San Francisco",
    "cityCode": "SFO",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "San Francisco International Airport",
        "airportCode": "SFO"
      }
    ]
  },
  {
    "cityName": "Cancun",
    "cityCode": "CUN",
    "countryName": "Mexico",
    "countryCode": "MX",
    "airports": [
      {
        "airportName": "Cancún International Airport",
        "airportCode": "CUN"
      }
    ]
  },
  {
    "cityName": "Las Vegas",
    "cityCode": "LAS",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "Harry Reid International Airport",
        "airportCode": "LAS"
      }
    ]
  },
  {
    "cityName": "Orlando",
    "cityCode": "ORL",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "Orlando International Airport",
        "airportCode": "MCO"
      },
      {
        "airportName": "Orlando Sanford International Airport",
        "airportCode": "SFB"
      }
    ]
  },
  {
    "cityName": "Boston",
    "cityCode": "BOS",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "Logan International Airport",
        "airportCode": "BOS"
      },
      {
        "airportName": "Portsmouth International at Pease Airport",
        "airportCode": "PSM"
      }
    ]
  },
  {
    "cityName": "Havana",
    "cityCode": "HAV",
    "countryName": "Cuba",
    "countryCode": "CU",
    "airports": [
      {
        "airportName": "José Martí International Airport",
        "airportCode": "HAV"
      }
    ]
  },
  {
    "cityName": "Kingston",
    "cityCode": "KIN",
    "countryName": "Jamaica",
    "countryCode": "JM",
    "airports": [
      {
        "airportName": "Norman Manley International Airport",
        "airportCode": "KIN"
      }
    ]
  },
  {
    "cityName": "Rio de Janeiro",
    "cityCode": "RIO",
    "countryName": "Brazil",
    "countryCode": "BR",
    "airports": [
      {
        "airportName": "Rio Galeão – Tom Jobim International Airport",
        "airportCode": "GIG"
      },
      {
        "airportName": "Santos Dumont Airport",
        "airportCode": "SDU"
      }
    ]
  },
  {
    "cityName": "Buenos Aires",
    "cityCode": "BUE",
    "countryName": "Argentina",
    "countryCode": "AR",
    "airports": [
      {
        "airportName": "Jorge Newbery Airpark",
        "airportCode": "AEP"
      },
      {
        "airportName": "Minister Pistarini International Airport",
        "airportCode": "EZE"
      }
    ]
  },
  {
    "cityName": "Santiago",
    "cityCode": "SCL",
    "countryName": "Chile",
    "countryCode": "CL",
    "airports": [
      {
        "airportName": "Comodoro Arturo Merino Benítez International Airport",
        "airportCode": "SCL"
      }
    ]
  },
  {
    "cityName": "Santiago",
    "cityCode": "STI",
    "countryName": "Dominican Republic",
    "countryCode": "DO",
    "airports": [
      {
        "airportName": "Cibao International Airport",
        "airportCode": "STI"
      }
    ]
  },
  {
    "cityName": "Lima",
    "cityCode": "LIM",
    "countryName": "Peru",
    "countryCode": "PE",
    "airports": [
      {
        "airportName": "Jorge Chávez International Airport",
        "airportCode": "LIM"
      }
    ]
  },
  {
    "cityName": "Quito",
    "cityCode": "UIO",
    "countryName": "Ecuador",
    "countryCode": "EC",
    "airports": [
      {
        "airportName": "Mariscal Sucre International Airport",
        "airportCode": "UIO"
      }
    ]
  },
  {
    "cityName": "Caracas",
    "cityCode": "CCS",
    "countryName": "Venezuela",
    "countryCode": "VE",
    "airports": [
      {
        "airportName": "Maiquetía Simón Bolívar International Airport",
        "airportCode": "CCS"
      }
    ]
  },
  {
    "cityName": "Montevideo",
    "cityCode": "MVD",
    "countryName": "Uruguay",
    "countryCode": "UY",
    "airports": [
      {
        "airportName": "Carrasco General Cesáreo L. Berisso International Airport",
        "airportCode": "MVD"
      }
    ]
  },
  {
    "cityName": "La Paz",
    "cityCode": "LPB",
    "countryName": "Bolivia",
    "countryCode": "BO",
    "airports": [
      {
        "airportName": "El Alto International Airport",
        "airportCode": "LPB"
      }
    ]
  },
  {
    "cityName": "La Paz",
    "cityCode": "LAP",
    "countryName": "Mexico",
    "countryCode": "MX",
    "airports": [
      {
        "airportName": "Manuel Márquez de León International Airport",
        "airportCode": "LAP"
      }
    ]
  },
  {
    "cityName": "Johannesburg",
    "cityCode": "JNB",
    "countryName": "South Africa",
    "countryCode": "ZA",
    "airports": [
      {
        "airportName": "O.R. Tambo International Airport",
        "airportCode": "JNB"
      },
      {
        "airportName": "Lanseria International Airport",
        "airportCode": "HLA"
      }
    ]
  },
  {
    "cityName": "Cairo",
    "cityCode": "CAI",
    "countryName": "Egypt",
    "countryCode": "EG",
    "airports": [
      {
        "airportName": "Cairo International Airport",
        "airportCode": "CAI"
      }
    ]
  },
  {
    "cityName": "Nairobi",
    "cityCode": "NBO",
    "countryName": "Kenya",
    "countryCode": "KE",
    "airports": [
      {
        "airportName": "Jomo Kenyatta International Airport",
        "airportCode": "NBO"
      },
      {
        "airportName": "Nairobi Wilson Airport",
        "airportCode": "WIL"
      }
    ]
  },
  {
    "cityName": "Casablanca",
    "cityCode": "CMN",
    "countryName": "Morocco",
    "countryCode": "MA",
    "airports": [
      {
        "airportName": "Mohammed V International Airport",
        "airportCode": "CMN"
      }
    ]
  },
  {
    "cityName": "Addis Ababa",
    "cityCode": "ADD",
    "countryName": "Ethiopia",
    "countryCode": "ET",
    "airports": [
      {
        "airportName": "Addis Ababa Bole International Airport",
        "airportCode": "ADD"
      }
    ]
  },
  {
    "cityName": "Lagos",
    "cityCode": "LOS",
    "countryName": "Nigeria",
    "countryCode": "NG",
    "airports": [
      {
        "airportName": "Murtala Muhammed International Airport",
        "airportCode": "LOS"
      }
    ]
  },
  {
    "cityName": "Cape Town",
    "cityCode": "CPT",
    "countryName": "South Africa",
    "countryCode": "ZA",
    "airports": [
      {
        "airportName": "Cape Town International Airport",
        "airportCode": "CPT"
      }
    ]
  },
  {
    "cityName": "Algiers",
    "cityCode": "ALG",
    "countryName": "Algeria",
    "countryCode": "DZ",
    "airports": [
      {
        "airportName": "Houari Boumediene Airport",
        "airportCode": "ALG"
      }
    ]
  },
  {
    "cityName": "Tunis",
    "cityCode": "TUN",
    "countryName": "Tunisia",
    "countryCode": "TN",
    "airports": [
      {
        "airportName": "Tunis Carthage International Airport",
        "airportCode": "TUN"
      }
    ]
  },
  {
    "cityName": "Accra",
    "cityCode": "ACC",
    "countryName": "Ghana",
    "countryCode": "GH",
    "airports": [
      {
        "airportName": "Kotoka International Airport",
        "airportCode": "ACC"
      }
    ]
  },
  {
    "cityName": "Dakar",
    "cityCode": "DKR",
    "countryName": "Senegal",
    "countryCode": "SN",
    "airports": [
      {
        "airportName": "Blaise Diagne International Airport",
        "airportCode": "DSS"
      }
    ]
  },
  {
    "cityName": "Entebbe",
    "cityCode": "EBB",
    "countryName": "Uganda",
    "countryCode": "UG",
    "airports": [
      {
        "airportName": "Entebbe International Airport",
        "airportCode": "EBB"
      }
    ]
  },
  {
    "cityName": "Dar es Salaam",
    "cityCode": "DAR",
    "countryName": "Tanzania",
    "countryCode": "TZ",
    "airports": [
      {
        "airportName": "Julius Nyerere International Airport",
        "airportCode": "DAR"
      }
    ]
  },
  {
    "cityName": "Sydney",
    "cityCode": "SYD",
    "countryName": "Australia",
    "countryCode": "AU",
    "airports": [
      {
        "airportName": "Sydney Kingsford Smith International Airport",
        "airportCode": "SYD"
      },
      {
        "airportName": "Palm Beach Seaplane Base",
        "airportCode": "LBH"
      }
    ]
  },
  {
    "cityName": "Sydney",
    "cityCode": "YQY",
    "countryName": "Canada",
    "countryCode": "CA",
    "airports": [
      {
        "airportName": "Sydney / J.A. Douglas McCurdy Airport",
        "airportCode": "YQY"
      }
    ]
  },
  {
    "cityName": "Melbourne",
    "cityCode": "MEL",
    "countryName": "Australia",
    "countryCode": "AU",
    "airports": [
      {
        "airportName": "Melbourne Airport",
        "airportCode": "MEL"
      },
      {
        "airportName": "Melbourne Avalon International Airport",
        "airportCode": "AVV"
      },
      {
        "airportName": "Melbourne Essendon Airport",
        "airportCode": "MEB"
      }
    ]
  },
  {
    "cityName": "Melbourne",
    "cityCode": "MLB",
    "countryName": "United States",
    "countryCode": "US",
    "airports": [
      {
        "airportName": "Melbourne Orlando International Airport",
        "airportCode": "MLB"
      }
    ]
  },
  {
    "cityName": "Auckland",
    "cityCode": "AKL",
    "countryName": "New Zealand",
    "countryCode": "NZ",
    "airports": [
      {
        "airportName": "Auckland International Airport",
        "airportCode": "AKL"
      }
    ]
  },
  {
    "cityName": "Brisbane",
    "cityCode": "BNE",
    "countryName": "Australia",
    "countryCode": "AU",
    "airports": [
      {
        "airportName": "Brisbane International Airport",
        "airportCode": "BNE"
      }
    ]
  },
  {
    "cityName": "Perth",
    "cityCode": "PER",
    "countryName": "Australia",
    "countryCode": "AU",
    "airports": [
      {
        "airportName": "Perth International Airport",
        "airportCode": "PER"
      }
    ]
  },
  {
    "cityName": "Christchurch",
    "cityCode": "CHC",
    "countryName": "New Zealand",
    "countryCode": "NZ",
    "airports": [
      {
        "airportName": "Christchurch International Airport",
        "airportCode": "CHC"
      }
    ]
  },
  {
    "cityName": "Wellington",
    "cityCode": "WLG",
    "countryName": "New Zealand",
    "countryCode": "NZ",
    "airports": [
      {
        "airportName": "Wellington International Airport",
        "airportCode": "WLG"
      }
    ]
  },
  {
    "cityName": "Port Moresby",
    "cityCode": "POM",
    "countryName": "Papua New Guinea",
    "countryCode": "PG",
    "airports": [
      {
        "airportName": "Port Moresby Jacksons International Airport",
        "airportCode": "POM"
      }
    ]
  },
  {
    "cityName": "Tahiti",
    "cityCode": "PPT",
    "countryName": "French Polynesia",
    "countryCode": "PF",
    "airports": [
      {
        "airportName": "Faa'a International Airport",
        "airportCode": "PPT"
      }
    ]
  }
];